import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { urlApi } from "../helpers/functionHelpers";
import useEmpresaStore from "../context/empresaStore";

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { setEmpresaId, setEmpresaIds } = useEmpresaStore();

  const cerrarSesion = useCallback(() => {
    //console.warn("🚪 Sesión expirada. Redirigiendo al login...");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const renovarToken = useCallback(async () => {
    const refreshToken = localStorage.getItem("refresh_token");

    if (!refreshToken) {
      //console.error("No hay refresh token disponible");
      cerrarSesion(); // Si no hay refresh token, cerramos sesión
      return null;
    }

    try {
      //console.log("Intentando renovar el token...");
      const response = await fetch(`${urlApi}/api/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();

        // Guardar el nuevo token
        localStorage.setItem("auth_token", data.accessToken);
        //console.log("Token renovado correctamente");

        return data.accessToken;
      } else {
        //console.error("Error al renovar el token");
        cerrarSesion();
        return null;
      }
    } catch (error) {
      //console.error("Error en la renovación del token:", error.message);
      cerrarSesion();
      return null;
    }
  }, [cerrarSesion]);

  // ✅ Función para cerrar sesión
  

  const authenticateUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");

      if (!token) {
        //console.warn("No hay token disponible");
        setLoading(false);
        return;
      }

      // Decodificar el token
      let decodedToken = jwtDecode(token);
      let accessToken = decodedToken.accessToken;

      if (!accessToken) {
        //console.warn("No hay accessToken en el token");
        setLoading(false);
        return;
      }

      // ✅ Verificar si el token ha expirado
      const decodedAccessToken = jwtDecode(accessToken);
      const exp = decodedAccessToken.exp * 1000; // Convertir a milisegundos
      const ahora = Date.now();

      if (exp < ahora) {
        //console.warn("Token caducado, intentando renovar...");
        accessToken = await renovarToken();

        if (!accessToken) {
          //console.error("No se pudo renovar el token");
          setLoading(false);
          return;
        }
      }

      // ✅ Decodificar el token renovado
      const decoded = jwtDecode(accessToken);
      const userData = {
        name: decoded.name,
        email: decoded.upn,
        oid: decoded.oid,
      };

      // ✅ Verificar si el usuario está en la base de datos
      const response = await fetch(`${urlApi}/api/auth/check-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Error en autenticación");
      }

      if (data.exists) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        setEmpresaId(data.user.empresa_id);
        setEmpresaIds(data.user.empresa_ids || [data.user.empresa_id]);
        
        if (data.user.role_id > 0 && location.pathname === "/login") {
          navigate("/admin", { replace: true });
        }
      } else {
        setUser(null);
        setEmpresaId(null);
      }
    } catch (err) {
      //error("❌ Error en autenticación:", err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [navigate, location, renovarToken, setEmpresaId]);

  useEffect(() => {
    authenticateUser();
  }, [authenticateUser]);

  return { user, loading, cerrarSesion, renovarToken };
};
